/**
 * Dictionnaire i18n du "chrome" des pages chauffeur dynamiques (FR/EN).
 *
 * Pourquoi : les sections (Hero, Benefits, Process…) portaient des libellés
 * codés en dur en français. Pour rendre la version EN 100 % anglaise SANS
 * dupliquer les composants, chaque section lit ses libellés ici via `locale`.
 * Les composants gardent `locale = "fr"` par défaut → aucune régression FR.
 *
 * Ne contient QUE des libellés d'interface réutilisables + les contenus de
 * repli (defaults). Le contenu propre à chaque page vit dans les slugs
 * (slugs/ pour FR, slugs-en/ pour EN). Voir ChauffeurSlugView.
 */
import { type I18n } from "@/config/localeText";

export type ChauffeurSlugUi = {
  // Hero (bloc sr-only crawlable)
  srTitle: string;
  srText: string;
  // ServicesSection
  servicesEyebrow: string;
  // BenefitsSection
  benefitsEyebrow: string;
  benefitsIntro: string;
  // ServiceCommitmentSection
  commitmentEyebrow: string;
  commitmentTitle: string;
  commitmentText: string;
  commitmentDefaultBadges: string[];
  // ProcessTimelineSection
  processEyebrow: string;
  stepWord: string;
  processDefaultHeading: string;
  processDefaultSteps: string[];
  // LocationsSection
  locationsEyebrow: string;
  locationsSrPrefix: string;
  // GenericInfo
  genericEyebrow: string;
  genericCtaLabel: string;
  genericFooter: string;
  genericFallback: string;
  // SlugFaqSection
  faqEyebrow: string;
  faqHeading: string;
  faqAsideEyebrow: string;
  faqAsideTitle: string;
  faqAsideText: string;
  faqAsideNote: string;
  faqDefault: { q: string; a: string }[];
  // ShowcaseGrid
  showcaseEyebrow: string;
  showcaseHeading: string;
  showcaseSubheading: string;
  useCaseLabel: (n: number) => string;
  useCaseTag: string;
  // CtaSection
  ctaEyebrow: string;
  ctaSecondaryQuote: string;
  ctaSecondaryBook: string;
  // FieldPreviewSection
  fieldPreviewTitle: string;
  fieldPreviewLeftAlt: string;
  fieldPreviewRightAlt: string;
  // Breadcrumbs + "Voir aussi"
  crumbHome: string;
  crumbChauffeur: string;
  seeAlsoHeading: string;
  seeAlsoText: string;
  quickAccessLabel: string;
  relatedDefault: { label: string; href: string }[];
  // Liens / hrefs locale-aware
  homeHref: string;
  chauffeurHref?: string; // undefined en EN tant que /en/chauffeur n'existe pas
  reservationHref: string;
  contactHref: string;
};

export const CHAUFFEUR_SLUG_UI: I18n<ChauffeurSlugUi> = {
  fr: {
    srTitle: "Chauffeur privé et VTC sur la Côte d’Azur",
    srText:
      "Chauffeur privé et VTC premium disponibles pour transferts aéroports, événements, business et mises à disposition. Service haut de gamme, ponctuel et confortable sur Nice, Cannes, Monaco et l’ensemble de la Côte d’Azur.",
    servicesEyebrow: "Formats disponibles",
    benefitsEyebrow: "Atouts clefs du service",
    benefitsIntro:
      "Des avantages concrets pour un service chauffeur fiable, lisible et adapté à vos contraintes terrain.",
    commitmentEyebrow: "Engagement service",
    commitmentTitle: "Un niveau de service constant",
    commitmentText:
      "Un service pensé pour rester lisible, ponctuel et professionnel dans des contextes exigeants: transferts, événements, déplacements business et demandes sur-mesure.",
    commitmentDefaultBadges: [
      "Ponctualité 24/7",
      "Accueil premium",
      "Coordination dédiée",
    ],
    processEyebrow: "Processus de prise en charge",
    stepWord: "Étape",
    processDefaultHeading: "Comment se déroule votre réservation",
    processDefaultSteps: [
      "Brief du besoin et validation des contraintes de trajet.",
      "Proposition du format de service adapté.",
      "Confirmation écrite avec point de prise en charge.",
      "Exécution et suivi jusqu'à destination.",
    ],
    locationsEyebrow: "Zones desservies",
    locationsSrPrefix: "Zones desservies: ",
    genericEyebrow: "Points cles du service",
    genericCtaLabel: "Réserver maintenant",
    genericFooter: "Accompagnement humain et exécution terrain",
    genericFallback:
      "Notre équipe met en œuvre des solutions souples et efficaces pour répondre à votre besoin.",
    faqEyebrow: "Questions fréquentes sur le service",
    faqHeading: "Questions fréquentes sur ce service",
    faqAsideEyebrow: "Conseil express",
    faqAsideTitle: "Un doute sur le bon format de service ?",
    faqAsideText:
      "Donnez votre contexte (aéroport, business, événement, volume passagers) et nous vous orientons vers la formule chauffeur la plus cohérente.",
    faqAsideNote: "Réponse humaine rapide, sans engagement.",
    faqDefault: [
      {
        q: "Quel est le délai de confirmation pour ce service ?",
        a: "Dans la majorité des cas, la confirmation intervient rapidement après validation des informations de trajet.",
      },
      {
        q: "Ce service est-il disponible en dehors des horaires standards ?",
        a: "Oui, selon disponibilité, nous organisons des prises en charge très matinales, tardives ou de nuit.",
      },
    ],
    showcaseEyebrow: "Exemples de missions",
    showcaseHeading: "Scénarios fréquents",
    showcaseSubheading:
      "Des situations concrètes dans lesquelles ce service est le plus utilisé.",
    useCaseLabel: (n) => `Cas d'usage ${n}`,
    useCaseTag: "Usage",
    ctaEyebrow: "Prise de contact",
    ctaSecondaryQuote: "Demander un devis",
    ctaSecondaryBook: "Réserver",
    fieldPreviewTitle: "Aperçu du service sur le terrain",
    fieldPreviewLeftAlt: "Contexte local de prise en charge",
    fieldPreviewRightAlt: "Exécution en conditions réelles",
    crumbHome: "Accueil",
    crumbChauffeur: "Chauffeur",
    seeAlsoHeading: "Voir aussi",
    seeAlsoText:
      "Comparez rapidement avec d'autres pages utiles pour choisir le bon format de service, consulter la flotte disponible ou contacter notre équipe.",
    quickAccessLabel: "Accès rapide",
    relatedDefault: [
      { label: "Explorer les excursions privées", href: "/excursions" },
      { label: "Voir les véhicules disponibles", href: "/location/vehicules" },
      { label: "Parler à un conseiller", href: "/contact" },
    ],
    homeHref: "/",
    chauffeurHref: "/chauffeur",
    reservationHref: "/reservation",
    contactHref: "/contact",
  },
  en: {
    srTitle: "Private chauffeur and VTC on the French Riviera",
    srText:
      "Premium private chauffeur and VTC available for airport transfers, events, business travel and hourly hire. High-end, punctual and comfortable service across Nice, Cannes, Monaco and the whole French Riviera.",
    servicesEyebrow: "Available formats",
    benefitsEyebrow: "Key service strengths",
    benefitsIntro:
      "Concrete advantages for a chauffeur service that is reliable, clear and tailored to your on-the-ground constraints.",
    commitmentEyebrow: "Service commitment",
    commitmentTitle: "A consistent level of service",
    commitmentText:
      "A service designed to stay clear, punctual and professional in demanding settings: transfers, events, business travel and bespoke requests.",
    commitmentDefaultBadges: [
      "24/7 punctuality",
      "Premium welcome",
      "Dedicated coordination",
    ],
    processEyebrow: "How we operate",
    stepWord: "Step",
    processDefaultHeading: "How your booking works",
    processDefaultSteps: [
      "Brief your need and confirm the trip constraints.",
      "We propose the most suitable service format.",
      "Written confirmation with the pick-up point.",
      "Execution and tracking through to your destination.",
    ],
    locationsEyebrow: "Areas served",
    locationsSrPrefix: "Areas served: ",
    genericEyebrow: "Key service points",
    genericCtaLabel: "Book now",
    genericFooter: "Human support and on-the-ground execution",
    genericFallback:
      "Our team puts flexible, effective solutions to work to meet your need.",
    faqEyebrow: "Frequently asked questions",
    faqHeading: "Frequently asked questions about this service",
    faqAsideEyebrow: "Quick tip",
    faqAsideTitle: "Not sure which service format fits?",
    faqAsideText:
      "Tell us your context (airport, business, event, number of passengers) and we'll point you to the most fitting chauffeur option.",
    faqAsideNote: "Fast human reply, no obligation.",
    faqDefault: [
      {
        q: "How long does it take to confirm this service?",
        a: "In most cases, confirmation comes quickly once the trip details are validated.",
      },
      {
        q: "Is this service available outside standard hours?",
        a: "Yes, subject to availability, we arrange very early, late or overnight pick-ups.",
      },
    ],
    showcaseEyebrow: "Example assignments",
    showcaseHeading: "Common scenarios",
    showcaseSubheading:
      "Concrete situations where this service is most often used.",
    useCaseLabel: (n) => `Use case ${n}`,
    useCaseTag: "Use case",
    ctaEyebrow: "Get in touch",
    ctaSecondaryQuote: "Request a quote",
    ctaSecondaryBook: "Book",
    fieldPreviewTitle: "The service in the field",
    fieldPreviewLeftAlt: "Local pick-up context",
    fieldPreviewRightAlt: "Execution in real conditions",
    crumbHome: "Home",
    crumbChauffeur: "Chauffeur",
    seeAlsoHeading: "See also",
    seeAlsoText:
      "Quickly compare with other useful pages to choose the right service format, browse the available fleet or contact our team.",
    quickAccessLabel: "Quick access",
    relatedDefault: [
      { label: "Explore private excursions", href: "/en/excursions" },
      { label: "See available vehicles", href: "/en/location/vehicules" },
      { label: "Talk to an advisor", href: "/contact" },
    ],
    homeHref: "/",
    chauffeurHref: "/en/chauffeur",
    reservationHref: "/en/reservation",
    contactHref: "/contact",
  },
  ar: {
    srTitle: "سائق خاص وسيارة VTC على الريفييرا الفرنسية",
    srText:
      "سائق خاص وسيارة VTC فاخرة متاحون للنقل من المطارات والفعاليات ورحلات الأعمال والخدمة تحت الطلب. خدمة راقية ودقيقة ومريحة في نيس وكان وموناكو وكامل الريفييرا الفرنسية.",
    servicesEyebrow: "الصيغ المتاحة",
    benefitsEyebrow: "أبرز مزايا الخدمة",
    benefitsIntro:
      "مزايا ملموسة لخدمة سائق موثوقة وواضحة ومصمَّمة وفق متطلباتكم الميدانية.",
    commitmentEyebrow: "التزام الخدمة",
    commitmentTitle: "مستوى خدمة ثابت",
    commitmentText:
      "خدمة مصمَّمة لتبقى واضحة ودقيقة واحترافية في السياقات المتطلِّبة: عمليات النقل، والفعاليات، ورحلات الأعمال، والطلبات المخصَّصة.",
    commitmentDefaultBadges: [
      "الالتزام بالمواعيد على مدار الساعة",
      "استقبال راقٍ",
      "تنسيق مخصَّص",
    ],
    processEyebrow: "آلية العمل",
    stepWord: "الخطوة",
    processDefaultHeading: "كيف تتم عملية الحجز",
    processDefaultSteps: [
      "عرض الحاجة وتأكيد متطلبات المسار.",
      "اقتراح صيغة الخدمة الأنسب.",
      "تأكيد كتابي مع نقطة الاستلام.",
      "التنفيذ والمتابعة حتى الوجهة.",
    ],
    locationsEyebrow: "المناطق المشمولة",
    locationsSrPrefix: "المناطق المشمولة: ",
    genericEyebrow: "النقاط الأساسية للخدمة",
    genericCtaLabel: "احجز الآن",
    genericFooter: "مرافقة بشرية وتنفيذ ميداني",
    genericFallback:
      "يضع فريقنا حلولاً مرنة وفعّالة قيد التنفيذ لتلبية حاجتكم.",
    faqEyebrow: "الأسئلة الشائعة",
    faqHeading: "الأسئلة الشائعة حول هذه الخدمة",
    faqAsideEyebrow: "نصيحة سريعة",
    faqAsideTitle: "غير متأكد من الصيغة المناسبة للخدمة؟",
    faqAsideText:
      "أخبرنا بسياقكم (مطار، أعمال، فعالية، عدد الركاب) وسنوجِّهكم إلى خيار السائق الأنسب.",
    faqAsideNote: "رد بشري سريع، دون أي التزام.",
    faqDefault: [
      {
        q: "كم يستغرق تأكيد هذه الخدمة؟",
        a: "في معظم الحالات، يتم التأكيد بسرعة بمجرد التحقق من تفاصيل المسار.",
      },
      {
        q: "هل تتوفر هذه الخدمة خارج ساعات العمل المعتادة؟",
        a: "نعم، رهناً بالتوفّر، ننظِّم عمليات استلام في وقت مبكر جداً أو متأخر أو ليلاً.",
      },
    ],
    showcaseEyebrow: "أمثلة على المهام",
    showcaseHeading: "سيناريوهات متكررة",
    showcaseSubheading: "حالات ملموسة تُستخدم فيها هذه الخدمة في الغالب.",
    useCaseLabel: (n) => `حالة استخدام ${n}`,
    useCaseTag: "حالة استخدام",
    ctaEyebrow: "تواصل معنا",
    ctaSecondaryQuote: "اطلب عرض سعر",
    ctaSecondaryBook: "احجز",
    fieldPreviewTitle: "الخدمة في الميدان",
    fieldPreviewLeftAlt: "سياق الاستلام المحلي",
    fieldPreviewRightAlt: "التنفيذ في ظروف واقعية",
    crumbHome: "الرئيسية",
    crumbChauffeur: "سائق خاص",
    seeAlsoHeading: "اطّلع أيضاً",
    seeAlsoText:
      "قارن بسرعة مع صفحات مفيدة أخرى لاختيار صيغة الخدمة المناسبة، أو لتصفّح الأسطول المتاح، أو للتواصل مع فريقنا.",
    quickAccessLabel: "وصول سريع",
    relatedDefault: [
      { label: "اكتشف الرحلات الخاصة", href: "/ar/excursions" },
      { label: "اطّلع على المركبات المتاحة", href: "/ar/location/vehicules" },
      { label: "تحدّث إلى مستشار", href: "/contact" },
    ],
    homeHref: "/",
    chauffeurHref: "/ar/chauffeur",
    reservationHref: "/ar/reservation",
    contactHref: "/contact",
  },
};
